import { appManager, AppState } from '../manager';
import { arrayToIndexedObject } from 'services/indexedObject';
import { notification } from 'antd';
import {
  type CaptureRequest,
  currentCaptureSnapshot,
  finishCaptureWrite,
  finishCaptureSlider,
  snapshotOverlapsCapture,
  startCaptureSnapshot,
  startCaptureWrite,
  startCaptureSlider,
  withCaptureRequest,
} from './captureRequests';

import {
  AssessmentDebugResponse,
  AssessmentInitialState,
  AssessmentOperationKind,
  AssessmentRecomputeOptimalResponse,
  AssessmentResultDetail,
  AssessmentResultsResponse,
  AssessmentRiskPanel,
  AssessmentScenarioOption,
  AssessmentSliderEvent,
  AssessmentStateResponse,
  AssessmentAssignment,
  AssessmentValidationResponse,
} from './types';

// pm.api pm/assessment/views.py — all endpoints keyed by SESSION id.
// `:session_id` is filled from the `session_id` selector merged into every
// payload by the manager (state.app.active_session), same as /sessions/... APIs.

export const latestSliderValues = (
  events: AssessmentSliderEvent[],
  currentPeriodId: number | null | undefined,
) => {
  // Backend scoring treats each period independently: no slider event in the
  // current period means the player is scored neutral (0). Only reflect events
  // from the current period so the UI never shows a stale prior-period value
  // as if it counted. `event.period` is the AssessmentPeriod database id, not
  // its display index (`current_period`). Events arrive ordered by created_at
  // and the last one wins. The fallback keeps the UI useful against an older
  // backend, which did not expose current_period_id; paired deployments always
  // take the exact-id branch.
  const values: { [code: string]: number } = {};
  (events || []).forEach(event => {
    if (currentPeriodId !== null && currentPeriodId !== undefined) {
      if (event.period !== currentPeriodId) return;
    }
    values[event.instrument_code] = event.value;
  });
  return values;
};

export const fetchAssessmentState = withCaptureRequest(
  appManager.createApi<CaptureRequest, AssessmentStateResponse, AppState>(
    'FETCH_ASSESSMENT_STATE',
    {
      path: '/assessment_sessions/:session_id/state',
      method: 'GET',
      startReducer(state, payload) {
        startCaptureSnapshot(state, payload);
        state.assessment.fetching = true;
        state.assessment.capture_error_scope = undefined;
      },
      successReducer(state, result, payload) {
        const sync = currentCaptureSnapshot(state, payload);
        if (!sync) return;
        const previous = state.assessment;
        if (result.assessment === null) {
          sync.pending = {};
          state.assessment = {
            ...AssessmentInitialState,
            risk: previous.risk,
            results: previous.results,
            resultDetails: previous.resultDetails,
            scenarios: previous.scenarios,
            fetched: true,
            capture_sync: sync,
            capture_scope: sync.scope,
          };
          return;
        }
        // A snapshot taken during a write cannot tell us whether that write was
        // committed yet. Preserve current evidence until an uncontended fetch;
        // metadata and a new period still hydrate normally.
        const preserveCapture =
          previous.capture_scope === sync.scope &&
          previous.scenario === result.scenario &&
          (result.current_period_id != null
            ? result.current_period_id === previous.current_period_id
            : result.current_period === previous.current_period) &&
          snapshotOverlapsCapture(sync);
        if (
          previous.capture_scope !== sync.scope ||
          previous.scenario !== result.scenario ||
          previous.current_period !== result.current_period ||
          previous.current_period_id !== result.current_period_id
        ) {
          // The assessment can advance before the session's socket tick arrives.
          // Retire the previous period's pending callbacks and slider baselines.
          sync.pending = {};
          sync.sliders = {};
        }
        state.assessment = {
          ...previous,
          attached: true,
          capture_only: result.capture_only === true,
          fetched: true,
          fetching: false,
          scenario: result.scenario,
          status: result.status,
          current_period: result.current_period,
          current_period_id: result.current_period_id,
          capture_scope: sync.scope,
          capture_error_scope: undefined,
          instruments: result.instruments || [],
          assignments: preserveCapture
            ? previous.assignments
            : arrayToIndexedObject(result.assignments || []),
          sliders: preserveCapture
            ? previous.sliders
            : latestSliderValues(
                result.sliders || [],
                result.current_period_id,
              ),
        };
      },
      failReducer(state, _error, payload) {
        const sync = currentCaptureSnapshot(state, payload);
        if (!sync) return;
        state.assessment.fetching = false;
        state.assessment.capture_error_scope = sync.scope;
      },
    },
  ),
);

// Optimistic: the bucket chip appears on drop (temp negative id) and is swapped
// for the server row on 201, or removed with a warning on failure. Extra payload
// fields (temp_id) are ignored by the backend serializer.
export const assignAssessmentNews = withCaptureRequest(
  appManager.createApi<
    { news: number; instrument_code: string; temp_id: number } & CaptureRequest,
    AssessmentAssignment,
    AppState
  >('ASSIGN_ASSESSMENT_NEWS', {
    path: '/assessment_sessions/:session_id/assign_news',
    method: 'POST',
    startReducer(state, payload) {
      startCaptureWrite(state, payload);
      state.assessment.assignments[payload.temp_id] = {
        id: payload.temp_id,
        news: payload.news,
        instrument_code: payload.instrument_code,
        period:
          state.assessment.current_period_id ??
          state.assessment.current_period ??
          0,
        created_at: new Date().toISOString(),
        revoked_at: null,
        pending: true,
      };
    },
    successReducer(state, result, payload) {
      const current = finishCaptureWrite(state, payload, result.period);
      delete state.assessment.assignments[payload.temp_id];
      if (!current) return;
      state.assessment.assignments[result.id] = result;
    },
    failReducer(state, error, payload) {
      const current = finishCaptureWrite(state, payload);
      delete state.assessment.assignments[payload.temp_id];
      if (!current) return;
      notification.warning({
        message: 'Could not assign news',
        description: String(error),
      });
    },
  }),
);

// Optimistic: the chip disappears immediately and is restored on failure.
export const revokeAssessmentAssignment = withCaptureRequest(
  appManager.createApi<
    { assignment: number } & CaptureRequest,
    AssessmentAssignment,
    AppState
  >('REVOKE_ASSESSMENT_ASSIGNMENT', {
    path: '/assessment_sessions/:session_id/revoke_assignment',
    method: 'POST',
    startReducer(state, payload) {
      startCaptureWrite(state, payload);
      const assignment = state.assessment.assignments[payload.assignment];
      if (assignment) assignment.revoked_at = new Date().toISOString();
    },
    successReducer(state, result, payload) {
      if (!finishCaptureWrite(state, payload, result.period)) return;
      delete state.assessment.assignments[result.id];
    },
    failReducer(state, error, payload) {
      if (!finishCaptureWrite(state, payload)) return;
      const assignment = state.assessment.assignments[payload.assignment];
      if (assignment) assignment.revoked_at = null;
      notification.warning({
        message: 'Could not remove assignment',
        description: String(error),
      });
    },
  }),
);

// Optimistic: the slider keeps the released value and rolls back on failure.
// prev_value rides the payload only for the rollback (ignored by the backend).
// `sliders` holds current-period values only, so writing the map here is the
// optimistic equivalent of storing the event under the current period; the
// next fetchAssessmentState (e.g. period rollover) re-derives it from the
// event log filtered by the new current_period.
export const setAssessmentSlider = withCaptureRequest(
  appManager.createApi<
    {
      instrument_code: string;
      value: number;
      prev_value?: number;
    } & CaptureRequest,
    AssessmentSliderEvent,
    AppState
  >('SET_ASSESSMENT_SLIDER', {
    path: '/assessment_sessions/:session_id/set_slider',
    method: 'POST',
    startReducer(state, payload) {
      startCaptureSlider(state, payload, payload.instrument_code);
      state.assessment.sliders[payload.instrument_code] = payload.value;
    },
    successReducer(state, result, payload) {
      finishCaptureSlider(state, payload, payload.instrument_code, result);
    },
    failReducer(state, error, payload) {
      if (!finishCaptureSlider(state, payload, payload.instrument_code)) return;
      notification.warning({
        message: 'Could not save conviction',
        description: String(error),
      });
    },
  }),
);

export const fetchAssessmentRisk = appManager.createApi<
  unknown,
  AssessmentRiskPanel,
  AppState
>('FETCH_ASSESSMENT_RISK', {
  path: '/assessment_sessions/:session_id/risk',
  method: 'GET',
  startReducer(state) {
    state.assessment.risk.fetching = true;
  },
  successReducer(state, result) {
    state.assessment.risk = { item: result, fetching: false, fetched: true };
  },
  failReducer(state) {
    state.assessment.risk.fetching = false;
  },
});

export const fetchAssessmentResults = appManager.createApi<
  unknown,
  AssessmentResultsResponse,
  AppState
>('FETCH_ASSESSMENT_RESULTS', {
  path: '/assessment_sessions/:session_id/results',
  method: 'GET',
  startReducer(state) {
    state.assessment.results.fetching = true;
  },
  successReducer(state, result) {
    state.assessment.results = {
      capture_only: result.capture_only === true,
      status: result.status,
      error: result.error,
      computed_at: result.computed_at,
      items: [...(result.results || [])].sort((a, b) => a.rank - b.rank),
      // The answer key's validity verdict travels with the scores it produced
      // (`validity` = the link snapshot stamped at attach; the product name is
      // read too, so a rename on either side cannot silently drop the warning
      // that stops a failed answer key wearing a hiring band). Kept exactly as
      // served: components/VerdictBanner/gate.ts is the only reader.
      answerKeyGate:
        result.validity !== undefined
          ? result.validity
          : result.answer_key_gate,
      fetching: false,
      fetched: true,
      recomputing: false,
    };
    // Refreshing results retries failed detail fetches (see resultDetails
    // docs in types.ts). In-flight (true) markers are left alone.
    const { fetching } = state.assessment.resultDetails;
    Object.keys(fetching).forEach(player => {
      if (!fetching[Number(player)]) delete fetching[Number(player)];
    });
  },
  failReducer(state) {
    state.assessment.results.fetching = false;
  },
});

// Per-player pillar breakdown for the trainer report tabs. Fetched lazily and
// cached by player id; failures are silent (the endpoint may land after this
// UI) — the Summary columns render a dash until data exists.
export const fetchAssessmentResultDetail = appManager.createApi<
  { player: number },
  AssessmentResultDetail,
  AppState
>('FETCH_ASSESSMENT_RESULT_DETAIL', {
  path: '/assessment_sessions/:session_id/result_detail',
  method: 'GET',
  startReducer(state, payload) {
    state.assessment.resultDetails.fetching[payload.player] = true;
  },
  successReducer(state, result, payload) {
    delete state.assessment.resultDetails.fetching[payload.player];
    state.assessment.resultDetails.items[result.player ?? payload.player] =
      result;
  },
  failReducer(state, error, payload) {
    // false = attempted & failed — effects must not auto-retry (see types.ts).
    state.assessment.resultDetails.fetching[payload.player] = false;
  },
});

export const recomputeAssessment = appManager.createApi<
  unknown,
  { detail: string },
  AppState
>('RECOMPUTE_ASSESSMENT', {
  path: '/assessment_sessions/:session_id/recompute',
  method: 'POST',
  startReducer(state) {
    state.assessment.results.recomputing = true;
  },
  successReducer(state) {
    state.assessment.results.status = 'pending';
    notification.info({
      message: 'Assessment recompute enqueued. Refresh results in a moment.',
    });
  },
  failReducer(state, error) {
    state.assessment.results.recomputing = false;
    notification.error({
      message: 'Could not start recompute',
      description: String(error),
    });
  },
});

export const fetchAssessmentScenarios = appManager.createApi<
  unknown,
  AssessmentScenarioOption[],
  AppState
>('FETCH_ASSESSMENT_SCENARIOS', {
  path: '/assessment_sessions/scenarios',
  method: 'GET',
  startReducer(state) {
    state.assessment.scenarios.fetching = true;
  },
  successReducer(state, result) {
    state.assessment.scenarios = {
      items: result || [],
      fetching: false,
      fetched: true,
    };
  },
  failReducer(state) {
    state.assessment.scenarios.fetching = false;
  },
});

// Admin-only QA payload behind the Debug tab. Without `player` the response
// is just the envelope (scenario/link/resolver map/players); with a player it
// also carries the per-period ledger. Cached like resultDetails: `fetching`
// markers are tri-state (absent = never asked, true = in flight, false =
// attempted & failed) so effects never loop; the tab's Refresh button
// re-dispatches unconditionally.
export const fetchAssessmentDebug = appManager.createApi<
  { player?: number },
  AssessmentDebugResponse,
  AppState
>('FETCH_ASSESSMENT_DEBUG', {
  path: '/assessment_sessions/:session_id/debug',
  method: 'GET',
  startReducer(state, payload) {
    if (payload?.player === undefined) {
      state.assessment.debug.envelopeFetching = true;
    } else {
      state.assessment.debug.fetching[payload.player] = true;
    }
  },
  successReducer(state, result, payload) {
    if (payload?.player === undefined) {
      state.assessment.debug.envelopeFetching = false;
      state.assessment.debug.envelope = result;
    } else {
      delete state.assessment.debug.fetching[payload.player];
      state.assessment.debug.items[result.player ?? payload.player] = result;
    }
  },
  failReducer(state, error, payload) {
    if (payload?.player === undefined) {
      state.assessment.debug.envelopeFetching = false;
    } else {
      state.assessment.debug.fetching[payload.player] = false;
    }
  },
});

// Admin-only scenario-level sanity diagnostics (endpoint lives in prod:
// pm.api validation/ -> services/validation_score.validation_report). Keyed
// by scenario code, one cached at a time.
export const fetchAssessmentValidation = appManager.createApi<
  { scenario: string },
  AssessmentValidationResponse,
  AppState
>('FETCH_ASSESSMENT_VALIDATION', {
  path: '/assessment_sessions/validation',
  method: 'GET',
  startReducer(state, payload) {
    state.assessment.validation.fetching = true;
    state.assessment.validation.scenario = payload.scenario;
  },
  successReducer(state, result) {
    state.assessment.validation = {
      item: result,
      scenario: result.scenario,
      fetching: false,
    };
  },
  failReducer(state) {
    state.assessment.validation.fetching = false;
  },
});

// Admin operations card (Debug tab). One operation remains: rebuilding this
// session's optimal book under its own limits (D6). Re-matching news and
// binding a blueprint by hand are retired -- the answer key is derived from
// the started-session shape and bound to its family by identity hash, so
// neither is an admin action any more. Results are stored on
// state.assessment.operations.lastResult (seq increments per completion so
// the tab can refetch the debug envelope exactly once); they render inline in
// the card because assessment actions are exempt from the global toast (see
// views/NotificationCenter/Operations.tsx).

const setOperationResult = (
  state: AppState,
  kind: AssessmentOperationKind,
  ok: boolean,
  message: string,
  description?: string,
) => {
  const { operations } = state.assessment;
  operations.lastResult = {
    seq: (operations.lastResult?.seq || 0) + 1,
    kind,
    ok,
    message,
    description,
  };
};

export const recomputeAssessmentOptimal = appManager.createApi<
  unknown,
  AssessmentRecomputeOptimalResponse,
  AppState
>('RECOMPUTE_ASSESSMENT_OPTIMAL', {
  path: '/assessment_sessions/:session_id/recompute_optimal',
  method: 'POST',
  startReducer(state) {
    state.assessment.operations.recomputingOptimal = true;
  },
  successReducer(state, result) {
    state.assessment.operations.recomputingOptimal = false;
    setOperationResult(
      state,
      'recompute_optimal',
      true,
      `Optimizer re-run: ${result.periods} periods, ${result.targets_written} targets written`,
      (result.notes || []).length ? (result.notes || []).join('; ') : undefined,
    );
  },
  failReducer(state, error) {
    state.assessment.operations.recomputingOptimal = false;
    setOperationResult(
      state,
      'recompute_optimal',
      false,
      'Could not re-run the optimizer',
      String(error),
    );
  },
});

export const attachAssessmentScenario = appManager.createApi<
  { scenario: string },
  { link: number; created: boolean },
  AppState
>('ATTACH_ASSESSMENT_SCENARIO', {
  path: '/assessment_sessions/:session_id/attach',
  method: 'POST',
  startReducer(state) {
    state.assessment.attaching = true;
  },
  successReducer(state, result, payload) {
    state.assessment.attaching = false;
    // Flag the snapshot stale — the badge/panels re-run fetchAssessmentState.
    state.assessment.fetched = false;
    notification.success({
      message: result.created
        ? `Assessment "${payload.scenario}" attached.`
        : 'Session already has an assessment attached.',
    });
  },
  failReducer(state, error) {
    state.assessment.attaching = false;
    notification.error({
      message: 'Could not attach assessment',
      description: String(error),
    });
  },
});
